import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useFetchGroup } from '../../hooks/fetch-group';
import { styled } from '@mui/material/styles';
import User from '../user/user';
import { Button, Typography, Box, Tab, Tabs } from '@mui/material';
import { joinGroup } from '../../services/group-services';
import { useAuth } from '../../hooks/useAuth';
import Comments from '../comments/comments';
import EventList from '../events/event-list';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import ContentPasteOffIcon from '@mui/icons-material/ContentPasteOff';

const MemberContainer = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr 6fr 1fr 1fr',
    alignItems: 'center',
    alignContent: "end"
});

const TrophyIcon = styled(EmojiEventsIcon)(({ trophy }) => ({
    color: trophy === 'gold' ? 'gold' : trophy === 'silver' ? 'silver' : trophy === 'bronze' ? '#cd7f32' : 'inherit'
}));

function GroupDetails() {

    const { authData } = useAuth();
    const { id } = useParams();
    const [data, loading, error] = useFetchGroup(id, authData.token);
    const [group, setGroup] = useState(null);
    const [isGroup, setInGroup] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [tabValue, setTabValue] = useState("members");
    const [filter, setFilter] = React.useState('future');
    const [filteredEvents, setFilteredEvents] = React.useState([]);

    const handleAlignment = (event, newFilter) => {
        setFilter(newFilter);

        const currentTime = new Date();
        let newEvents = group.events;
        if (newFilter === "future") {
            newEvents = group.events.filter(evt => currentTime < new Date(evt.time));
        } else if (newFilter === "past") {
            newEvents = group.events.filter(evt => currentTime > new Date(evt.time));
        }
        setFilteredEvents(newEvents.sort((a, b) => new Date(a.time) - new Date(b.time)));
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const navigate = useNavigate();

    useEffect(() => {
        if (data?.members) {

            data.members.sort((a, b) => b.points - a.points);

            const availableTrophies = ['gold', 'silver', 'bronze'];
            let currentTrophy = 0;
            let position = 0;

            data.members.forEach((m, indx) => {

                if (indx === 0) {
                    m.trophy = availableTrophies[0];
                    position++;
                } else {
                    if (m.points !== data.members[indx - 1].points) {
                        position++;
                        currentTrophy++;
                        if (indx >= 3) currentTrophy = 4;
                    }
                    if (currentTrophy < availableTrophies.length && position <= 3) {
                        m.trophy = availableTrophies[currentTrophy];
                    }
                }
                m.position = position;
            });

            if (authData?.user) {
                setInGroup(!!data.members.find(member => member.user.id === authData.user.id));
                setIsAdmin(data.members.find(member => member.user.id === authData.user.id)?.admin);
            }
        }
        setGroup(data);
        if (data && data.events) {
            const newEvents = data.events.filter(evt => new Date() < new Date(evt.time));
            setFilteredEvents(newEvents.sort((a, b) => new Date(a.time) - new Date(b.time)));
        }
    }, [data, authData])

    const joinHere = () => {
        joinGroup({ user: authData.user.id, group: group.id }).then(
            res => { console.log(res) }
        )
    }

    const addEvent = () => {
        navigate('/event-form', { state: { group } })
    }

    if (error) return <h1>Error</h1>
    if (loading) return <h1>Loading....</h1>


    return (
        <div>
            {group &&
                <TabContext value={tabValue}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Link to={`/`} style={{ display: 'flex', alignItems: 'center', marginRight: '1rem' }}>
                            <ChevronLeftIcon />
                        </Link>
                        <Typography variant="h2" sx={{ flexGrow: 1 }}>{group.name}</Typography>
                        {isGroup ?
                            <div>
                                {isAdmin && tabValue === "events" &&
                                    <Button onClick={() => addEvent()} variant="contained" color="primary" size="small">
                                        Add Event
                                    </Button>}
                            </div>
                            :
                            <Button onClick={() => joinHere()} variant="contained" color="primary" size="small">
                                Join Group
                            </Button>
                        }
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }} mb={2}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth"
                            indicatorColor="primary" textColor="primary">
                            <Tab label="Members" value="members" sx={{ textTransform: 'none', fontWeight: 600 }} />
                            <Tab label="Events" value="events" sx={{ textTransform: 'none', fontWeight: 600 }} />
                            <Tab label="Comments" value="comments" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        </Tabs>
                    </Box>
                    <TabPanel value="members" sx={{ p: 0 }}>
                        {group.members.map((member) => (
                            <MemberContainer key={member.id} sx={{
                                p: 1.5,
                                mb: 1,
                                backgroundColor: 'background.paper',
                                borderRadius: 2,
                                border: '1px solid rgba(255,255,255,0.05)'
                            }}>
                                <Typography variant="body2" color="text.secondary">{member.position}.</Typography>
                                <User user={member.user} />
                                <Typography variant="body1" fontWeight={600}>{member.points} pts</Typography>
                                {member.trophy ? <TrophyIcon trophy={member.trophy} /> : <Box />}
                            </MemberContainer>
                        ))}
                    </TabPanel>
                    <TabPanel value="events">
                        <ToggleButtonGroup
                            value={filter}
                            exclusive
                            onChange={handleAlignment}
                            aria-label="text alignment"
                            className={"pb-2"}
                        >
                            <ToggleButton value="all">
                                <ContentPasteIcon />
                            </ToggleButton>
                            <ToggleButton value="future">
                                <ContentPasteGoIcon />
                            </ToggleButton>
                            <ToggleButton value="past">
                                <ContentPasteOffIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <EventList events={filteredEvents} />
                    </TabPanel>
                    <TabPanel value="comments">
                        <Comments group={group} />
                    </TabPanel>
                </TabContext>
            }

        </div>
    );
}

export default GroupDetails;
